/* Small building blocks the dashboard-style pages share: the reference card,
 * its label/value rows, tabs with an underline, the range chips, the
 * two-letter mode badge and the dark product pill. All of them are thin
 * wrappers over classes in theme.css so the admin console and the terminal
 * draw them identically.
 */
import { useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { Icon, type IconName } from '@/components/Icons'
import type { TradingMode } from '@/types'

export function InfoDot({ text }: { text: string }) {
  return (
    <button type="button" className="qx-info" title={text} aria-label={text}
            onClick={(e) => e.preventDefault()}>
      i
    </button>
  )
}

export function Card({ title, info, actions, children, footer, noPadding, style }: {
  title?: string
  info?: string
  actions?: ReactNode
  children: ReactNode
  footer?: ReactNode
  noPadding?: boolean
  style?: CSSProperties
}) {
  return (
    <section className="qx-card" style={{ display: 'flex', flexDirection: 'column', minWidth: 0, ...style }}>
      {title && (
        <header className="qx-card-head">
          <h2 className="qx-card-title" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {title}
            {info && <InfoDot text={info} />}
          </h2>
          {actions && <div className="qx-card-actions">{actions}</div>}
        </header>
      )}
      <div className={noPadding ? undefined : 'qx-card-body'} style={{ flex: 1, minWidth: 0 }}>
        {children}
      </div>
      {footer && <footer className="qx-card-foot">{footer}</footer>}
    </section>
  )
}

export function KvRow({ icon, label, info, value, sub, tone }: {
  icon?: IconName
  label: string
  info?: string
  value: ReactNode
  sub?: ReactNode
  tone?: 'accent' | 'ok' | 'warn' | 'danger' | 'neutral'
}) {
  const color =
    tone === 'ok' ? 'var(--ok)' :
    tone === 'warn' ? 'var(--warn)' :
    tone === 'danger' ? 'var(--danger)' :
    tone === 'neutral' ? 'var(--text-primary)' : 'var(--accent)'
  return (
    <div className="qx-kv">
      <span className="qx-kv-label">
        {icon && <Icon name={icon} size={16} />}
        {label}
        {info && <InfoDot text={info} />}
      </span>
      <span className="qx-kv-value" style={{ color }}>
        {value}
        {sub && <span className="sub">{sub}</span>}
      </span>
    </div>
  )
}

export function Tabs<T extends string>({ value, onChange, items }: {
  value: T
  onChange: (v: T) => void
  items: { id: T; label: string; icon?: IconName; count?: number }[]
}) {
  return (
    <div className="qx-tabs" role="tablist" style={{ borderBottom: 'none' }}>
      {items.map((t) => (
        <button key={t.id} role="tab" aria-selected={value === t.id}
                className={`qx-tab${value === t.id ? ' on' : ''}`}
                onClick={() => onChange(t.id)}>
          {t.icon && <Icon name={t.icon} size={15} />}
          {t.label}
          {t.count !== undefined && <span className="count">{t.count}</span>}
        </button>
      ))}
    </div>
  )
}

/* ---------- range chips ----------
   1D … All Time, as the reference draws them. `includes(ts)` is what a
   caller filters with; "All" accepts everything. */
export type RangeId = '1D' | '3D' | '7D' | '1M' | '3M' | '6M' | '1Y' | 'ALL'
const RANGE_MS: Record<RangeId, number | null> = {
  '1D': 864e5, '3D': 3 * 864e5, '7D': 7 * 864e5, '1M': 30 * 864e5,
  '3M': 90 * 864e5, '6M': 182 * 864e5, '1Y': 365 * 864e5, ALL: null,
}
export const RANGE_IDS: RangeId[] = ['1D', '3D', '7D', '1M', '3M', '6M', '1Y', 'ALL']

export interface Range {
  id: RangeId
  set: (id: RangeId) => void
  includes: (ts: string | number | null | undefined) => boolean
}

export function useRange(initial: RangeId = '7D'): Range {
  const [id, set] = useState<RangeId>(initial)
  return useMemo(() => ({
    id, set,
    includes: (ts) => {
      const span = RANGE_MS[id]
      if (span === null) return true
      if (ts == null) return false
      const t = typeof ts === 'number' ? ts : Date.parse(ts)
      return Number.isFinite(t) && Date.now() - t <= span
    },
  }), [id])
}

export function RangeChips({ range }: { range: Range }) {
  return (
    <span className="qx-chips" role="radiogroup" aria-label="Range">
      {RANGE_IDS.map((r) => (
        <button key={r} role="radio" aria-checked={range.id === r}
                className={`qx-chip${range.id === r ? ' on' : ''}`}
                onClick={() => range.set(r)}>
          {r === 'ALL' ? 'All Time' : r}
        </button>
      ))}
    </span>
  )
}

/** The two-letter badge: BT purple, PT orange, LT teal. The letters carry the
 *  meaning; the colour reinforces it. */
export function ModeDot({ mode, title }: { mode: TradingMode; title?: string }) {
  const code = mode === 'BACKTEST' ? 'BT' : mode === 'PAPER' ? 'PT' : 'LT'
  const label = mode === 'BACKTEST' ? 'Backtest' : mode === 'PAPER' ? 'Paper trading' : 'Live trading'
  return (
    <span className={`qx-mode ${mode}`} title={title ?? label} aria-label={label}>{code}</span>
  )
}

export function ProductPill({ label }: { label: string }) {
  return <span className="qx-pill-dark">{label}</span>
}

/** The outlined, tinted stat tile from the reference's My Portfolio page. */
export function Tile({ tone, icon, value, label, info, onClick }: {
  tone: 'green' | 'pink' | 'orange' | 'teal' | 'cyan' | 'blue' | 'red'
  icon: IconName
  value: string
  label: string
  info?: string
  onClick?: () => void
}) {
  const clickable = Boolean(onClick)
  return (
    <div className={`qx-tile qx-tile-${tone}${clickable ? ' clickable' : ''}`}
         role={clickable ? 'button' : undefined} tabIndex={clickable ? 0 : undefined}
         onClick={onClick} title={info}
         onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick?.() } : undefined}>
      <Icon name={icon} size={30} style={{ strokeWidth: 1.6 }} />
      <div style={{ minWidth: 0 }}>
        <span className="value num">{value}</span>
        <span className="label">
          {label}
          {info && <span className="qx-info" aria-hidden>i</span>}
          {clickable && <Icon name="external" size={13} />}
        </span>
      </div>
    </div>
  )
}
